import { useMediaQuery } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { sendTrackingEvent } from "../../../helpers/tracking_management";
import { fetchPaidPartnershipPageContent } from "../../../redux/actions/company_action";
import { PAID_LANDING_PAGE_BANNER_FRAGMENT } from "../paidLandingPageStrapiFragment";
import {
  LandingBannerContainer,
  LandingBannerCta,
  LandingBannerCtaText,
  LandingBannerImage,
  LandingBannerInner,
  LandingBannerLeft,
  LandingBannerPartnerContainer,
  LandingBannerPartnerLogo,
  LandingBannerPartnerName,
  LandingBannerRight,
  LandingBannerSubtitle,
  LandingBannerTitle,
  LandingBannerTitleCard,
} from "./styles";

export default function LandingBanner({ initialBanner = null }) {
  const [data, setData] = useState(initialBanner ?? null);
  const dispatch = useDispatch();
  const isTablet = useMediaQuery("(max-width:768px)");
  const isMobile = useMediaQuery("(max-width:600px)");
  const isDesktop = useMediaQuery("(min-width: 1240px)");
  const bannerData = Array.isArray(data) ? data[0] : data;
  const isImageOnly = bannerData?.layout === "imageOnly";

  useEffect(() => {
    if (initialBanner != null) return;

    let cancelled = false;

    async function load() {
      const response = await dispatch(
        fetchPaidPartnershipPageContent(PAID_LANDING_PAGE_BANNER_FRAGMENT)
      );
      const { banner } = response || {};
      if (!cancelled && banner) setData(banner);
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [dispatch, initialBanner]);

  const handleExploreClick = () => {
    sendTrackingEvent({
      event: "CE_paid_landing_banner_cta_click",
      origin: "paid_landing_page",
    });

    if (bannerData?.ctaLinkRedirection) {
      window.location.href = bannerData.ctaLinkRedirection;
      return;
    }

    const target = document.getElementById("LandingFeaturedJobs");
    if (!target) return;

    const headerOffset = 110;
    const rect = target.getBoundingClientRect();
    const offsetPosition = rect.top + window.pageYOffset - headerOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  };

  const webImage = bannerData?.bannerImageWeb?.data?.attributes?.url;
  const tabletImage = bannerData?.bannerImageTablet?.data?.attributes?.url;
  const mobileImage = bannerData?.bannerImageMobile?.data?.attributes?.url;

  const bannerImageSrc = isMobile
    ? mobileImage || tabletImage || webImage
    : isTablet
    ? tabletImage || mobileImage || webImage
    : webImage;

  const clientCompanyLogo =
    bannerData?.clientCompanyLogo?.data?.attributes?.url;
  const clientCompanyName = bannerData?.clientCompanyName;
  const isPartner = clientCompanyLogo && clientCompanyName;

  const bannerTitleHtml =
    typeof bannerData?.bannerTitle === "string" &&
    bannerData.bannerTitle.trim() !== ""
      ? bannerData.bannerTitle
      : "";

  const bannerImageAlt =
    bannerTitleHtml
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim() || "Landing banner";

  if (isImageOnly) {
    if (!bannerImageSrc) return null;

    return (
      <LandingBannerContainer $imageOnly>
        <LandingBannerImage
          $imageOnly
          src={bannerImageSrc}
          alt={bannerImageAlt}
        />
      </LandingBannerContainer>
    );
  }

  return (
    <LandingBannerContainer>
      <LandingBannerInner>
        <LandingBannerLeft>
          <LandingBannerTitleCard>
            {isPartner && (
              <LandingBannerPartnerContainer>
                <LandingBannerPartnerLogo
                  src={clientCompanyLogo}
                  alt={clientCompanyName}
                />
                <LandingBannerPartnerName>
                  {clientCompanyName}
                </LandingBannerPartnerName>
              </LandingBannerPartnerContainer>
            )}
            {bannerTitleHtml ? (
              <LandingBannerTitle
                dangerouslySetInnerHTML={{ __html: bannerTitleHtml }}
              />
            ) : null}
            {bannerData?.bannerSubtitle ? (
              <LandingBannerSubtitle
                dangerouslySetInnerHTML={{ __html: bannerData.bannerSubtitle }}
              />
            ) : null}
          </LandingBannerTitleCard>
          {isDesktop && (
            <LandingBannerCta type="button" onClick={handleExploreClick}>
              <LandingBannerCtaText>
                {bannerData?.ctaButtonLabel ?? "Explore Programs"}
              </LandingBannerCtaText>
            </LandingBannerCta>
          )}
        </LandingBannerLeft>
        {bannerImageSrc ? (
          <LandingBannerRight>
            <LandingBannerImage src={bannerImageSrc} alt={bannerImageAlt} />
          </LandingBannerRight>
        ) : null}

        {!isDesktop && (
          <LandingBannerCta type="button" onClick={handleExploreClick}>
            <LandingBannerCtaText>
              {bannerData?.ctaButtonLabel ?? "Explore Programs"}
            </LandingBannerCtaText>
          </LandingBannerCta>
        )}
      </LandingBannerInner>
    </LandingBannerContainer>
  );
}
